// ============================================================================
// useDesignVersion — single source of truth for the V1 / V3 site theme.
//
// Used by:
//   - src/pages/LandingPage.jsx        (renders LandingPageV1 or LandingPageV3)
//   - src/pages/Dashboard.jsx          (renders DashboardV1 or DashboardV3)
//   - src/components/Navbar.jsx        (renders NavbarV1 or NavbarV3)
//
// First render reads from localStorage (instant, no flicker).
// In the background, fetches the authoritative version from the
// public.get_landing_design_version() RPC. If it differs, swaps and
// caches the new value. Defaults to 'v3' (the new viral redesign) when
// no value is found or the RPC fails.
// ============================================================================
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const DEFAULT_VERSION = 'v3';
const STORAGE_KEY = 'landing_design_version_cache';

export function useDesignVersion() {
  const [version, setVersion] = useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      return (cached === 'v1' || cached === 'v3') ? cached : DEFAULT_VERSION;
    } catch {
      return DEFAULT_VERSION;
    }
  });

  useEffect(() => {
    let cancelled = false;
    supabase.rpc('get_landing_design_version').then(({ data, error }) => {
      if (cancelled || error) return;
      const next = typeof data === 'string' ? data : data?.version;
      if (next && (next === 'v1' || next === 'v3') && next !== version) {
        setVersion(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
      }
    }, () => { /* noop */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return version;
}
